import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { legalLabels } from "../i18n/legal";
import { consultNav } from "../i18n/consult";

export default function Footer() {
  const { t, lang } = useLang();
  const f = t.footer;
  return (
    <footer className="border-t border-sovereign/10 bg-obsidian relative overflow-hidden" data-testid="footer">
      <div className="absolute inset-0 hud-lines opacity-30 pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid md:grid-cols-[1.4fr_1fr_1fr_1.2fr] gap-12">
          <div>
            <div className="flex items-center gap-3">
              <img src="/logo.svg" alt="" className="w-10 h-10" />
              <span className="leading-none">
                <span className="block font-display font-extrabold text-slate-50">EMAN TEAM</span>
                <span className="block font-mono text-[9px] tracking-[0.42em] text-sovereign mt-1">DYNAMICS</span>
              </span>
            </div>
            <p className="mt-6 font-mono text-xs tracking-[0.2em] uppercase text-slate-400">{f.tagline}</p>
            <p className="mt-4 text-sm text-slate-500 leading-relaxed max-w-xs">{f.sub}</p>
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-sovereign font-bold">{f.navTitle}</p>
            <ul className="mt-5 space-y-3">
              {[
                { to: "/", label: f.links.home, tid: "footer-link-home" },
                { to: "/partners", label: f.links.partners, tid: "footer-link-partners" },
                { to: "/contact", label: f.links.contact, tid: "footer-link-contact" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={l.tid} className="text-sm text-slate-400 hover:text-sovereign transition-colors duration-300">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-sovereign font-bold">{f.techTitle}</p>
            <ul className="mt-5 space-y-3">
              {[
                { to: "/ibm-solutions", label: f.links.ibm, tid: "footer-link-ibm" },
                { to: "/konect-systems", label: f.links.konect, tid: "footer-link-konect" },
                { to: "/3eeco", label: f.links.eeco, tid: "footer-link-eeco" },
                { to: "/consultanta-audit", label: consultNav[lang], tid: "footer-link-consult" },
                { to: "/datacenter-ai", label: f.links.dc, tid: "footer-link-dc" },
                { to: "/critical-infrastructure", label: f.links.infra, tid: "footer-link-infra" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={l.tid} className="text-sm text-slate-400 hover:text-sovereign transition-colors duration-300">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-sovereign font-bold">{f.contactTitle}</p>
            <a
              href="mailto:office@emanteam.eu"
              data-testid="footer-email"
              className="mt-5 inline-flex items-center gap-2.5 text-sm text-slate-200 hover:text-sovereign transition-colors duration-300"
            >
              <Mail className="w-4 h-4 text-sovereign shrink-0" aria-hidden="true" />
              {f.email}
            </a>
            <div className="mt-4 space-y-2">
              {["+40 755 163 503", "+44 7846 777 864"].map((p, i) => (
                <a
                  key={i}
                  href={`tel:${p.replace(/\s/g, "")}`}
                  data-testid={`footer-phone-${i}`}
                  className="flex items-center gap-2.5 text-sm text-slate-300 hover:text-sovereign transition-colors duration-300"
                >
                  <Phone className="w-4 h-4 text-sovereign shrink-0" aria-hidden="true" />
                  {p}
                </a>
              ))}
            </div>
            <p className="mt-4 flex items-start gap-2.5 text-xs text-slate-400 leading-relaxed" data-testid="footer-address">
              <MapPin className="w-4 h-4 mt-0.5 text-sovereign shrink-0" aria-hidden="true" />
              Bd. Pipera nr. I-VIII, et.3, Voluntari, Ilfov, CP: 077190
            </p>
            <p className="mt-5 text-xs text-slate-600 font-mono">www.emanteam.eu</p>
          </div>
        </div>
        <div className="mt-16 pt-8 border-t border-slate-800/60 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <p className="text-xs text-slate-600 font-mono">© {new Date().getFullYear()} {f.legal}. {f.rights}</p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="footer-legal-links">
            <Link to="/termeni" data-testid="footer-legal-terms" className="text-xs text-slate-500 hover:text-sovereign transition-colors duration-300">{legalLabels[lang].terms}</Link>
            <Link to="/gdpr" data-testid="footer-legal-gdpr" className="text-xs text-slate-500 hover:text-sovereign transition-colors duration-300">{legalLabels[lang].privacy}</Link>
            <Link to="/cookies" data-testid="footer-legal-cookies" className="text-xs text-slate-500 hover:text-sovereign transition-colors duration-300">{legalLabels[lang].cookies}</Link>
          </div>
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-600">IBM • KONECTSYSTEMS • 3EECO</p>
        </div>
      </div>
    </footer>
  );
}
